import { __awaiter, __decorate, __extends, __generator, __metadata } from "tslib";
import { MdcComponent } from '@aurelia-mdc-web/base';
import { MDCCheckboxFoundation } from '@material/checkbox';
import { bindable } from 'aurelia-typed-observable-plugin';
import { getCorrectEventName } from '@material/animation/util';
import { inject, useView, PLATFORM, customElement, bindingMode } from 'aurelia-framework';
var checkboxId = 0;
/**
 * @selector mdc-checkbox
 * @emits change | Event dispatched on checked change.
 */
var MdcCheckbox = /** @class */ (function (_super) {
    __extends(MdcCheckbox, _super);
    function MdcCheckbox(root) {
        var _this = _super.call(this, root) || this;
        _this.id = "mdc-checkbox-".concat(++checkboxId, "-input");
        /**
         * Whether the checkbox should go to checked state or unchecked when toggled from indeterminate state.
         */
        _this.indeterminateToChecked = true;
        defineMdcCheckboxElementApis(_this.root);
        return _this;
    }
    MdcCheckbox.prototype.disabledChanged = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initialised];
                    case 1:
                        _a.sent();
                        // still need to check because a component might already be destroyed
                        // by the time the binding is applied
                        if (this.nativeControl_) {
                            this.nativeControl_.disabled = this.disabled;
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(MdcCheckbox.prototype, "checked", {
        get: function () {
            var _a;
            if (this.nativeControl_) {
                return this.nativeControl_.checked;
            }
            else {
                return (_a = this._checked) !== null && _a !== void 0 ? _a : false;
            }
        },
        /**
         * Whether the checkbox is checked.
         */
        set: function (checked) {
            this._checked = checked;
            if (this.nativeControl_) {
                this.nativeControl_.checked = checked;
            }
        },
        enumerable: false,
        configurable: true
    });
    MdcCheckbox.prototype.indeterminateChanged = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.initialised];
                    case 1:
                        _b.sent();
                        if (this.nativeControl_) {
                            this.nativeControl_.indeterminate = this.indeterminate;
                        }
                        (_a = this.foundation) === null || _a === void 0 ? void 0 : _a.handleChange();
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(MdcCheckbox.prototype, "value", {
        get: function () {
            return this.nativeControl_.value;
        },
        set: function (value) {
            this.nativeControl_.value = value;
        },
        enumerable: false,
        configurable: true
    });
    // eslint-disable-next-line @typescript-eslint/require-await
    MdcCheckbox.prototype.initialise = function () {
        return __awaiter(this, void 0, void 0, function () {
            var attributeValue, attributeValue;
            return __generator(this, function (_a) {
                this.listen(getCorrectEventName(window, 'animationend'), this.handleAnimationEnd_);
                if (this.root.hasAttribute('checked')) {
                    attributeValue = this.root.getAttribute('checked');
                    if (attributeValue || attributeValue === '') {
                        this.root.checked = true;
                    }
                }
                if (this.root.hasAttribute('indeterminate')) {
                    attributeValue = this.root.getAttribute('indeterminate');
                    if (attributeValue || attributeValue === '') {
                        this.root.indeterminate = true;
                    }
                }
                return [2 /*return*/];
            });
        });
    };
    MdcCheckbox.prototype.initialSyncWithDOM = function () {
        if (this._checked !== undefined) {
            this.checked = this._checked;
        }
    };
    MdcCheckbox.prototype.destroy = function () {
        this.unlisten(getCorrectEventName(window, 'animationend'), this.handleAnimationEnd_);
        this._checked = undefined;
    };
    MdcCheckbox.prototype.handleChange_ = function () {
        var _a;
        if (this.indeterminate) {
            this.indeterminate = false;
            this.checked = this.indeterminateToChecked;
        }
        (_a = this.foundation) === null || _a === void 0 ? void 0 : _a.handleChange();
    };
    MdcCheckbox.prototype.handleAnimationEnd_ = function () {
        var _a;
        (_a = this.foundation) === null || _a === void 0 ? void 0 : _a.handleAnimationEnd();
    };
    MdcCheckbox.prototype.getDefaultFoundation = function () {
        var _this = this;
        // DO NOT INLINE this variable. For backward compatibility, foundations take a Partial<MDCFooAdapter>.
        // To ensure we don't accidentally omit any methods, we need a separate, strongly typed adapter variable.
        var adapter = {
            addClass: function (className) { return _this.root.classList.add(className); },
            forceLayout: function () { return (_this.root).offsetWidth; },
            hasNativeControl: function () { return !!_this.nativeControl_; },
            isAttachedToDOM: function () { return Boolean(_this.root.parentNode); },
            isChecked: function () { return _this.checked; },
            isIndeterminate: function () { return _this.indeterminate; },
            removeClass: function (className) {
                _this.root.classList.remove(className);
            },
            removeNativeControlAttr: function (attr) {
                _this.nativeControl_.removeAttribute(attr);
            },
            setNativeControlAttr: function (attr, value) {
                _this.nativeControl_.setAttribute(attr, value);
            },
            setNativeControlDisabled: function (disabled) {
                _this.nativeControl_.disabled = disabled;
            },
        };
        return new MDCCheckboxFoundation(adapter);
    };
    /**
     * Set focus to the checkbox.
     */
    MdcCheckbox.prototype.focus = function () {
        this.nativeControl_.focus();
    };
    /**
     * Moves focus from the checkbox.
     */
    MdcCheckbox.prototype.blur = function () {
        this.nativeControl_.blur();
    };
    __decorate([
        bindable.booleanAttr,
        __metadata("design:type", Boolean)
    ], MdcCheckbox.prototype, "disabled", void 0);
    __decorate([
        bindable.booleanAttr,
        __metadata("design:type", Boolean)
    ], MdcCheckbox.prototype, "touch", void 0);
    __decorate([
        bindable.booleanAttr,
        __metadata("design:type", Boolean)
    ], MdcCheckbox.prototype, "disableRipple", void 0);
    __decorate([
        bindable.booleanAttr,
        __metadata("design:type", Boolean)
    ], MdcCheckbox.prototype, "indeterminateToChecked", void 0);
    __decorate([
        bindable.booleanAttr({ defaultBindingMode: bindingMode.twoWay }),
        __metadata("design:type", Boolean)
    ], MdcCheckbox.prototype, "indeterminate", void 0);
    MdcCheckbox = __decorate([
        inject(Element),
        useView(PLATFORM.moduleName('./mdc-checkbox.html')),
        customElement('mdc-checkbox'),
        __metadata("design:paramtypes", [Object])
    ], MdcCheckbox);
    return MdcCheckbox;
}(MdcComponent));
export { MdcCheckbox };
function defineMdcCheckboxElementApis(element) {
    Object.defineProperties(element, {
        type: {
            value: 'checkbox',
        },
        checked: {
            get: function () {
                return this.au.controller.viewModel.checked;
            },
            set: function (value) {
                this.au.controller.viewModel.checked = value;
            },
            configurable: true
        },
        focus: {
            value: function () {
                this.au.controller.viewModel.focus();
            },
            configurable: true
        },
        blur: {
            value: function () {
                this.au.controller.viewModel.blur();
            },
            configurable: true
        }
    });
}
//# sourceMappingURL=mdc-checkbox.js.map